import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from '@/components/ui/use-toast';

export interface SupplierReturnNote {
  id: string;
  code: string;
  grn_id?: string;
  po_id?: string;
  supplier_id?: string;
  status: 'DRAFT' | 'PENDING' | 'DISPATCHED' | 'COMPLETED' | 'CANCELLED';
  return_date: string;
  dispatch_date?: string;
  return_reason?: string;
  transport_mode?: string;
  vehicle_number?: string;
  driver_name?: string;
  driver_contact?: string;
  notes?: string;
  replacement_expected: boolean;
  credit_note_number?: string;
  credit_note_amount?: number;
  created_by?: string;
  dispatched_by?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  grn?: {
    id: string;
    code: string;
  };
  purchase_order?: {
    id: string;
    code: string;
  };
  supplier?: {
    id: string;
    name: string;
    code: string;
  };
  srn_lines?: SupplierReturnNoteLine[];
}

export interface SupplierReturnNoteLine {
  id: string;
  srn_id: string;
  grn_line_id?: string;
  item_id: string;
  qty_returned: number;
  uom: string;
  unit_price?: number;
  reject_reason?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  item?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface CreateSRNRequest {
  grn_id: string;
  po_id: string;
  supplier_id: string;
  return_date?: string;
  return_reason?: string;
  notes?: string;
  replacement_expected?: boolean;
  lines: Array<{
    grn_line_id: string;
    item_id: string;
    qty_returned: number;
    uom: string;
    unit_price?: number;
    reject_reason?: string;
  }>;
}

export const useSupplierReturnNote = () => {
  const [loading, setLoading] = useState(false);

  const fetchSRNs = useCallback(async (filters?: { status?: string; supplier_id?: string }) => {
    try {
      setLoading(true);
      let query = supabase
        .from('supplier_return_note')
        .select(`
          *,
          grn:grn(id, code),
          purchase_order:purchase_order(id, code),
          supplier:suppliers(id, name, code),
          srn_lines:supplier_return_note_line(
            id,
            grn_line_id,
            item_id,
            qty_returned,
            uom,
            unit_price,
            reject_reason,
            item:inventory_items(id, name, code)
          )
        `)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.supplier_id) {
        query = query.eq('supplier_id', filters.supplier_id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as SupplierReturnNote[];
    } catch (error) {
      console.error('Error fetching SRNs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch supplier return notes',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getSRN = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('supplier_return_note')
        .select(`
          *,
          grn:grn(id, code),
          purchase_order:purchase_order(id, code),
          supplier:suppliers(id, name, code),
          srn_lines:supplier_return_note_line(
            id,
            grn_line_id,
            item_id,
            qty_returned,
            uom,
            unit_price,
            reject_reason,
            item:inventory_items(id, name, code)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as SupplierReturnNote;
    } catch (error) {
      console.error('Error fetching SRN:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch supplier return note',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createSRN = useCallback(async (request: CreateSRNRequest) => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create SRN header
      const { data: srnData, error: srnError } = await supabase
        .from('supplier_return_note')
        .insert({
          code: '', // Will be auto-generated by trigger
          grn_id: request.grn_id,
          po_id: request.po_id,
          supplier_id: request.supplier_id,
          return_date: request.return_date || new Date().toISOString().split('T')[0],
          return_reason: request.return_reason,
          notes: request.notes,
          replacement_expected: request.replacement_expected || false,
          created_by: user.id,
        })
        .select()
        .single();

      if (srnError) throw srnError;

      // Create SRN lines
      const lines = request.lines.map(line => ({
        srn_id: srnData.id,
        grn_line_id: line.grn_line_id,
        item_id: line.item_id,
        qty_returned: line.qty_returned,
        uom: line.uom,
        unit_price: line.unit_price,
        reject_reason: line.reject_reason,
      }));

      const { error: linesError } = await supabase
        .from('supplier_return_note_line')
        .insert(lines);

      if (linesError) throw linesError;

      toast({
        title: 'Success',
        description: `Supplier Return Note ${srnData.code} created successfully`,
      });

      return srnData as SupplierReturnNote;
    } catch (error: any) {
      console.error('Error creating SRN:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create supplier return note',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSRNStatus = useCallback(async (id: string, status: string) => {
    try {
      setLoading(true);

      const updateData: any = { status };

      // If dispatching, set dispatch date and user
      if (status === 'DISPATCHED') {
        const { data: { user } } = await supabase.auth.getUser();
        updateData.dispatch_date = new Date().toISOString();
        updateData.dispatched_by = user?.id;
      }

      const { error } = await supabase
        .from('supplier_return_note')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Status updated to ${status}`,
      });

      return true;
    } catch (error: any) {
      console.error('Error updating SRN status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update status',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSRN = useCallback(async (id: string, data: Partial<SupplierReturnNote>) => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('supplier_return_note')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier return note updated',
      });

      return true;
    } catch (error: any) {
      console.error('Error updating SRN:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update supplier return note',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSRN = useCallback(async (id: string) => {
    try {
      setLoading(true);

      // Check if SRN is in DRAFT status
      const { data: srn } = await supabase
        .from('supplier_return_note')
        .select('status')
        .eq('id', id)
        .single();

      if (srn?.status !== 'DRAFT') {
        throw new Error('Only draft SRNs can be deleted');
      }

      const { error } = await supabase
        .from('supplier_return_note')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier return note deleted',
      });

      return true;
    } catch (error: any) {
      console.error('Error deleting SRN:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete supplier return note',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    fetchSRNs,
    getSRN,
    createSRN,
    updateSRN,
    updateSRNStatus,
    deleteSRN,
  };
};
